import Phaser from 'phaser';
import { COLORS, GAME_HEIGHT, GAME_WIDTH } from '../config/constants';
import { audio } from '../core/AudioSynth';
import { music } from '../core/MusicSystem';
import { localMusic } from '../core/LocalMusicPlayer';
import { saveManager, type GameSettings } from '../core/SaveManager';
import { exitGame } from '../core/ExitGame';
import { voice } from '../core/Voice';
import { addAudioToggleButtons, addRetroButtonHover, drawScanlines } from '../ui/Retro';
import { applyUiTextScale } from '../ui/UiText';

type Tab = 'gameplay' | 'video' | 'fidelity' | 'audio' | 'controls';
type BoolKey = 'sfx' | 'music' | 'voice' | 'shake' | 'slowmo' | 'damageNumbers' | 'grid' | 'scanlines' | 'autoWaves' | 'devMode';

const TABS: { id: Tab; label: string }[] = [
  { id: 'gameplay', label: 'GAMEPLAY' },
  { id: 'video', label: 'VIDEO' },
  { id: 'fidelity', label: 'FIDELITY' },
  { id: 'audio', label: 'AUDIO' },
  { id: 'controls', label: 'CONTROLS' },
];

const CX = 350;
const CW = 880;

export class PauseScene extends Phaser.Scene {
  private tab: Tab = 'gameplay';
  private from = 'Menu';
  private inBattle = false;
  private playlistOpen = false;
  /** Staged copy of the settings; committed only when APPLY is pressed. */
  private draft!: GameSettings;

  constructor() {
    super('Pause');
  }

  init(data: { from?: string }): void {
    this.from = data?.from ?? 'Menu';
    this.inBattle = this.from === 'Battle';
    this.tab = 'gameplay';
    this.playlistOpen = false;
  }

  create(): void {
    audio.unlock();
    this.draft = JSON.parse(JSON.stringify(saveManager.data.settings)) as GameSettings;
    this.input.keyboard?.on('keydown-ESC', () => this.resume());
    this.input.keyboard?.on('keydown-H', () => {
      this.scene.stop();
      this.scene.resume(this.from);
      const manual = this.scene.get(this.from) as Phaser.Scene & { fieldManual?: { toggle(): void } };
      manual?.fieldManual?.toggle?.();
    });
    this.render();
  }

  // ---- chrome ----

  private render(): void {
    this.children.removeAll();
    const g = this.add.graphics();
    g.fillStyle(0x05080b, 0.9);
    g.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    if (this.textures.exists('ui-command-grid')) {
      this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'ui-command-grid')
        .setDisplaySize(GAME_WIDTH, GAME_HEIGHT)
        .setAlpha(0.11)
        .setBlendMode(Phaser.BlendModes.SCREEN);
    }
    drawScanlines(g, GAME_WIDTH, GAME_HEIGHT, 0.05, 4);

    // Header
    g.fillStyle(COLORS.panel, 1);
    g.fillRect(0, 0, GAME_WIDTH, 64);
    g.lineStyle(1, COLORS.panelEdge, 1);
    g.lineBetween(0, 64, GAME_WIDTH, 64);
    g.fillStyle(COLORS.accent, 0.8);
    g.fillRect(0, 62, 180, 2);

    this.add.text(40, 22, 'SYSTEM MENU', {
      fontFamily: 'Consolas, monospace',
      fontSize: '20px',
      color: '#5ec8ff',
      letterSpacing: 3,
    });
    this.add
      .text(GAME_WIDTH - 40, 30, this.inBattle ? 'MISSION PAUSED' : 'OPTIONS', {
        fontFamily: 'Consolas, monospace',
        fontSize: '11px',
        color: '#6a7a88',
      })
      .setOrigin(1, 0.5);
    addAudioToggleButtons(this, 330, 22);

    // Tab column
    TABS.forEach((t, i) => {
      const y = 110 + i * 52;
      const active = t.id === this.tab;
      const rect = this.add
        .rectangle(40, y, 250, 42, active ? 0x152028 : 0x0e141a, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(1, active ? COLORS.accent : COLORS.panelEdge)
        .setInteractive({ useHandCursor: true });
      if (active) {
        g.fillStyle(COLORS.accent, 1);
        g.fillRect(40, y, 3, 42);
      }
      this.add.text(58, y + 13, t.label, {
        fontFamily: 'Consolas, monospace',
        fontSize: '13px',
        color: active ? '#5ec8ff' : '#8aa0b0',
        letterSpacing: 1,
      });
      rect.on('pointerover', () => !active && rect.setFillStyle(0x121a20));
      rect.on('pointerout', () => !active && rect.setFillStyle(0x0e141a));
      rect.on('pointerdown', () => {
        this.tab = t.id;
        audio.click();
        this.render();
      });
    });

    const archiveButton = (y: number, label: string, mode: 'intro' | 'credits') => {
      const rect = this.add
        .rectangle(40, y, 250, 36, 0x0e141a, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(1, COLORS.panelEdge)
        .setInteractive({ useHandCursor: true });
      this.add.text(58, y + 11, label, {
        fontFamily: 'Consolas, monospace',
        fontSize: '11px',
        color: '#8aa0b0',
        letterSpacing: 1,
      });
      rect.on('pointerover', () => rect.setFillStyle(0x121a20));
      rect.on('pointerout', () => rect.setFillStyle(0x0e141a));
      rect.on('pointerdown', () => this.openStory(mode));
    };
    archiveButton(382, 'FIELD INTRO', 'intro');
    archiveButton(428, 'CREDITS', 'credits');

    // Content frame
    g.fillStyle(0x0b1116, 0.92);
    g.fillRect(CX - 20, 92, CW + 40, 512);
    g.lineStyle(1, COLORS.panelEdge, 1);
    g.strokeRect(CX - 20, 92, CW + 40, 512);
    const b = 14;
    g.lineStyle(2, COLORS.accent, 0.4);
    g.lineBetween(CX - 20, 92, CX - 20 + b, 92);
    g.lineBetween(CX - 20, 92, CX - 20, 92 + b);
    g.lineBetween(CX + CW + 20, 92, CX + CW + 20 - b, 92);
    g.lineBetween(CX + CW + 20, 92, CX + CW + 20, 92 + b);

    this.add.text(CX, 108, this.tab.toUpperCase(), {
      fontFamily: 'Consolas, monospace',
      fontSize: '16px',
      color: '#d4a24a',
      letterSpacing: 2,
    });

    const pending = this.hasPending();
    if (pending) {
      this.add
        .text(GAME_WIDTH - 40, 46, '● UNAPPLIED CHANGES', {
          fontFamily: 'Consolas, monospace',
          fontSize: '11px',
          color: '#7dce5a',
        })
        .setOrigin(1, 0.5);
    }

    // Bottom action buttons
    const actions: { label: string; colour: number; on: () => void }[] = [
      { label: 'RESUME', colour: COLORS.accent, on: () => this.resume() },
      {
        label: pending ? 'APPLY  ●' : 'APPLY',
        colour: pending ? COLORS.good : 0x2a3e4a,
        on: () => this.commit(),
      },
    ];
    if (this.inBattle) {
      actions.push({ label: 'RESTART MISSION', colour: COLORS.warn, on: () => this.restartMission() });
    }
    if (this.from !== 'Menu') {
      actions.push({ label: this.inBattle ? 'ABANDON → MENU' : 'MAIN MENU', colour: 0x8aa0b0, on: () => this.toMenu() });
    }
    actions.push({
      label: 'EXIT GAME',
      colour: 0x6a7a88,
      on: () => {
        audio.click();
        exitGame();
      },
    });

    const totalW = actions.length * 210 + (actions.length - 1) * 16;
    let ax = GAME_WIDTH / 2 - totalW / 2;
    for (const a of actions) {
      const rect = this.add
        .rectangle(ax, 636, 210, 44, 0x152028, 1)
        .setOrigin(0, 0.5)
        .setStrokeStyle(1.5, a.colour)
        .setInteractive({ useHandCursor: true });
      this.add
        .text(ax + 105, 636, a.label, {
          fontFamily: 'Consolas, monospace',
          fontSize: '12px',
          color: '#c8d4e0',
        })
        .setOrigin(0.5);
      rect.on('pointerover', () => rect.setFillStyle(0x1a2830));
      rect.on('pointerout', () => rect.setFillStyle(0x152028));
      rect.on('pointerdown', a.on);
      ax += 226;
    }

    switch (this.tab) {
      case 'gameplay':
        this.renderGameplay(g);
        break;
      case 'video':
        this.renderVideo(g);
        break;
      case 'fidelity':
        this.renderFidelity(g);
        break;
      case 'audio':
        this.renderAudio(g);
        break;
      case 'controls':
        this.renderControls(g);
        break;
    }
    applyUiTextScale(this);
  }

  private openStory(mode: 'intro' | 'credits'): void {
    audio.click();
    this.scene.pause();
    this.scene.launch('Story', { mode, returnTo: 'Pause', markSeen: mode === 'intro' });
    // Story is registered before Pause in the Phaser scene stack, so bring it
    // above this paused settings surface when opened from the sidebar.
    this.scene.bringToTop('Story');
  }

  // ---- rows ----

  private toggleRow(y: number, label: string, note: string, key: BoolKey): void {
    const s = this.draft;
    const on = s[key];
    this.add.text(CX, y, label, {
      fontFamily: 'Consolas, monospace',
      fontSize: '13px',
      color: '#c8d4e0',
    });
    this.add.text(CX, y + 17, note, {
      fontFamily: 'Consolas, monospace',
      fontSize: '10px',
      color: '#4a5560',
    });
    const chip = this.add
      .rectangle(CX + CW - 90, y + 8, 84, 28, 0x152028, 1)
      .setStrokeStyle(1, on ? COLORS.accent : 0x2a3e4a)
      .setInteractive({ useHandCursor: true });
    addRetroButtonHover(chip);
    this.add
      .text(CX + CW - 90, y + 8, on ? 'ON' : 'OFF', {
        fontFamily: 'Consolas, monospace',
        fontSize: '12px',
        color: on ? '#7dce5a' : '#6a7a88',
      })
      .setOrigin(0.5);
    chip.on('pointerdown', () => {
      s[key] = !on;
      audio.click();
      this.render();
    });
  }

  private choiceRow(
    y: number,
    label: string,
    note: string,
    key: 'particles' | 'scaleMode' | 'chatty' | 'uiTextScale' | 'cursorSize',
    options: { v: string; label: string }[],
  ): void {
    const s = this.draft;
    this.add.text(CX, y, label, {
      fontFamily: 'Consolas, monospace',
      fontSize: '13px',
      color: '#c8d4e0',
    });
    this.add.text(CX, y + 17, note, {
      fontFamily: 'Consolas, monospace',
      fontSize: '10px',
      color: '#4a5560',
    });
    const gap = options.length > 3 ? 78 : 92;
    const w = options.length > 3 ? 72 : 84;
    // Anchor the last choice inside the content frame instead of letting the
    // four-option CHATTY row spill past the right edge at FIT scale.
    let x = CX + CW - 60 - (options.length - 1) * gap;
    for (const opt of options) {
      const active = String(s[key]) === opt.v;
      const rect = this.add
        .rectangle(x, y + 8, w, 28, active ? 0x1a2830 : 0x152028, 1)
        .setStrokeStyle(1, active ? COLORS.accent : 0x2a3e4a)
        .setInteractive({ useHandCursor: true });
      addRetroButtonHover(rect, active ? 0x1a2830 : 0x152028);
      this.add
        .text(x, y + 8, opt.label, {
          fontFamily: 'Consolas, monospace',
          fontSize: options.length > 3 ? '9px' : '11px',
          color: active ? '#5ec8ff' : '#6a7a88',
        })
        .setOrigin(0.5);
      rect.on('pointerdown', () => {
        if (key === 'uiTextScale' || key === 'cursorSize') s[key] = Number(opt.v);
        else (s as unknown as Record<string, string>)[key] = opt.v;
        audio.click();
        this.render();
      });
      x += gap;
    }
  }

  private sliderRow(y: number, label: string, key: 'sfxVolume' | 'musicVolume' | 'voiceVolume'): void {
    const s = this.draft;
    const value = s[key];
    this.add.text(CX, y, label, {
      fontFamily: 'Consolas, monospace',
      fontSize: '13px',
      color: '#c8d4e0',
    });
    this.add
      .text(CX + CW - 90, y, `${Math.round(value * 100)}%`, {
        fontFamily: 'Consolas, monospace',
        fontSize: '12px',
        color: '#9ad4e8',
      })
      .setOrigin(1, 0);
    const trackX = CX;
    const trackW = CW - 130;
    const trackY = y + 30;
    const g = this.add.graphics();
    g.fillStyle(0x152028, 1);
    g.fillRect(trackX, trackY, trackW, 8);
    g.fillStyle(COLORS.accent, 0.9);
    g.fillRect(trackX, trackY, trackW * value, 8);
    g.fillStyle(0xffffff, 1);
    g.fillRect(trackX + trackW * value - 2, trackY - 4, 4, 16);
    const zone = this.add
      .rectangle(trackX, trackY + 4, trackW, 26, 0x000000, 0.001)
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true });
    const setFromPointer = (px: number) => {
      const v = Phaser.Math.Clamp((px - trackX) / trackW, 0, 1);
      s[key] = Math.round(v * 20) / 20;
      this.render();
    };
    zone.on('pointerdown', (p: Phaser.Input.Pointer) => setFromPointer(p.x));
    zone.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (p.isDown) setFromPointer(p.x);
    });
  }

  // ---- tabs ----

  private renderGameplay(_g: Phaser.GameObjects.Graphics): void {
    let y = 150;
    const gap = 62;
    this.toggleRow(y, 'AUTO WAVES', 'Waves advance automatically after a short delay', 'autoWaves');
    y += gap;
    this.toggleRow(y, 'SCREEN SHAKE', 'Camera kick on explosions and breaches', 'shake');
    y += gap;
    this.toggleRow(y, 'DAMAGE NUMBERS', 'Floating hit values over hostiles', 'damageNumbers');
    y += gap;
    this.toggleRow(y, 'DEV MODE', 'Unlocks Map Forge authoring and developer tuning tools', 'devMode');
    y += gap;
    this.add.text(CX, y + 4, this.draft.devMode
      ? 'OPEN MAP FORGE TO CONFIGURE GLOBAL TUNING, LANE WEIGHTS AND MAP SHOTS.'
      : 'TIP — click any built tower to open its upgrade, priority, evolution and symbiosis menu.', {
      fontFamily: 'Consolas, monospace',
      fontSize: '11px',
      color: '#6a7a88',
      wordWrap: { width: CW },
    });
  }

  private renderVideo(_g: Phaser.GameObjects.Graphics): void {
    let y = 150;
    this.choiceRow(y, 'DISPLAY MODE', 'FIT keeps the whole field on screen; FILL crops to the display', 'scaleMode', [
      { v: 'fit', label: 'FIT' },
      { v: 'fill', label: 'FILL' },
    ]);
    y += 62;
    this.fullscreenRow(y);
    y += 62;
    this.choiceRow(y, 'CURSOR SIZE', 'Chrome command cursor scale', 'cursorSize', [
      { v: '0.5', label: 'SMALL' },
      { v: '0.75', label: 'MEDIUM' },
      { v: '1', label: 'LARGE' },
    ]);
    y += 62;
    this.toggleRow(y, 'TACTICAL GRID', 'Grid overlay on the battlefield', 'grid');
    y += 62;
    this.toggleRow(y, 'SCANLINES', 'CRT scanline overlay', 'scanlines');
    y += 62;
    this.add.text(CX, y, 'Press F anywhere to toggle fullscreen. Display mode applies immediately.', {
      fontFamily: 'Consolas, monospace',
      fontSize: '11px',
      color: '#6a7a88',
      wordWrap: { width: CW },
    });
  }

  private fullscreenRow(y: number): void {
    this.add.text(CX, y, 'FULLSCREEN', {
      fontFamily: 'Consolas, monospace',
      fontSize: '13px',
      color: '#c8d4e0',
    });
    this.add.text(CX, y + 17, 'Toggle browser fullscreen (F)', {
      fontFamily: 'Consolas, monospace',
      fontSize: '10px',
      color: '#4a5560',
    });
    const on = this.game.scale.isFullscreen;
    const chip = this.add
      .rectangle(CX + CW - 90, y + 8, 84, 28, 0x152028, 1)
      .setStrokeStyle(1, COLORS.accent)
      .setInteractive({ useHandCursor: true });
    addRetroButtonHover(chip);
    this.add
      .text(CX + CW - 90, y + 8, on ? 'EXIT' : 'ENTER', {
        fontFamily: 'Consolas, monospace',
        fontSize: '12px',
        color: '#5ec8ff',
      })
      .setOrigin(0.5);
    chip.on('pointerdown', () => {
      audio.click();
      if (this.game.scale.isFullscreen) this.game.scale.stopFullscreen();
      else this.game.scale.startFullscreen();
      this.time.delayedCall(300, () => this.render());
    });
  }

  private renderFidelity(_g: Phaser.GameObjects.Graphics): void {
    let y = 150;
    this.choiceRow(y, 'PARTICLE DENSITY', 'Spark, smoke and debris volume', 'particles', [
      { v: 'low', label: 'LOW' },
      { v: 'medium', label: 'MED' },
      { v: 'high', label: 'HIGH' },
    ]);
    y += 62;
    this.toggleRow(y, 'SCREEN SHAKE', 'Camera kick on heavy impacts', 'shake');
    y += 62;
    this.toggleRow(y, 'SLOW-MOTION', 'Hit-stop on kills and heavy impacts', 'slowmo');
    y += 62;
    this.choiceRow(y, 'UI TEXT SIZE', 'Increase tactical labels and interface readouts', 'uiTextScale', [
      { v: '0.85', label: 'SMALL' },
      { v: '1', label: 'NORMAL' },
      { v: '1.15', label: 'LARGE' },
      { v: '1.3', label: 'XL' },
    ]);
    y += 62;
    this.add.text(CX, y, 'Lower fidelity if you see frame drops during large waves.', {
      fontFamily: 'Consolas, monospace',
      fontSize: '11px',
      color: '#6a7a88',
      wordWrap: { width: CW },
    });
  }

  private renderAudio(_g: Phaser.GameObjects.Graphics): void {
    if (this.playlistOpen) {
      this.renderPlaylist(140);
      return;
    }
    let y = 150;
    this.toggleRow(y, 'SOUND EFFECTS', 'Weapon fire, impacts and UI', 'sfx');
    y += 62;
    this.sliderRow(y, 'SFX VOLUME', 'sfxVolume');
    y += 62;
    this.toggleRow(y, 'MUSIC', 'Adaptive battle score', 'music');
    y += 62;
    this.sliderRow(y, 'MUSIC VOLUME', 'musicVolume');
    y += 62;
    this.toggleRow(y, 'VOICE', 'Commander & tactical AI callouts', 'voice');
    y += 62;
    this.sliderRow(y, 'VOICE VOLUME', 'voiceVolume');
    y += 62;
    this.choiceRow(y, 'CHATTY', 'How often the crew talks to you', 'chatty', [
      { v: 'normal', label: 'NORMAL' },
      { v: 'sparse', label: 'SPARSE' },
      { v: 'warnings', label: 'WARN' },
      { v: 'off', label: 'OFF' },
    ]);
    this.playlistButton(CX + 220, 578, 220, 'OPEN PLAYLIST MANAGER', () => { this.playlistOpen = true; this.render(); }, 0x1d4a5c);
  }

  private renderPlaylist(y: number): void {
    const authored = music.authoredPlaylist;
    const local = localMusic.snapshot();
    this.add.rectangle(CX + CW / 2, 370, CW - 26, 438, 0x09141b, 0.98).setStrokeStyle(1.5, 0x315162);
    this.add.text(CX, y, 'PLAYLIST CONTROL', {
      fontFamily: 'Consolas, monospace', fontSize: '13px', color: '#d4a24a', letterSpacing: 1,
    });
    this.add.text(CX, y + 18, 'Preview, reorder or remove authored battle tracks. Local commander tracks stay below.', {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#6a7a88',
    });
    authored.forEach((name, index) => {
      const rowY = y + 48 + index * 27;
      this.add.text(CX, rowY, `${index + 1}. ${name}`, { fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#b8d9e5' });
      this.playlistButton(CX + 330, rowY + 4, 62, 'PREVIEW', () => music.previewAuthoredTrack(index));
      this.playlistButton(CX + 398, rowY + 4, 24, '↑', () => { music.moveAuthoredTrack(index, Math.max(0, index - 1)); this.render(); });
      this.playlistButton(CX + 426, rowY + 4, 24, '↓', () => { music.moveAuthoredTrack(index, Math.min(authored.length - 1, index + 1)); this.render(); });
      this.playlistButton(CX + 454, rowY + 4, 28, '×', () => { music.removeAuthoredTrack(index); this.render(); }, 0x3d161c);
    });
    const visibleLocal = local.tracks.slice(0, 5);
    const localY = y + 58 + authored.length * 27;
    this.playlistButton(CX + 330, localY + 4, 124, music.isAuthoredShuffle ? 'AUTO SHUFFLE ON' : 'SEQUENTIAL', () => { music.toggleAuthoredShuffle(); this.render(); }, music.isAuthoredShuffle ? 0x1d4a5c : 0x152028);
    this.add.text(CX, localY, `LOCAL PLAYLIST  ·  ${local.tracks.length ? local.tracks.length + ' TRACKS' : 'NO IMPORTED TRACKS'}`, { fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#d4a24a' });
    if (local.tracks.length) {
      visibleLocal.forEach((name, index) => {
        const rowY = localY + 26 + index * 25;
        this.add.text(CX, rowY, `${index + 1}. ${name.slice(0, 42)}`, { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9fb2c0' });
        this.playlistButton(CX + 330, rowY + 4, 62, index === local.index && local.playing ? 'PAUSE' : 'PLAY', () => { if (index !== local.index) localMusic.selectTrack(index); else localMusic.toggle(); });
        this.playlistButton(CX + 398, rowY + 4, 24, '↑', () => { localMusic.move(index, Math.max(0, index - 1)); this.render(); });
        this.playlistButton(CX + 426, rowY + 4, 24, '↓', () => { localMusic.move(index, Math.min(local.tracks.length - 1, index + 1)); this.render(); });
        this.playlistButton(CX + 454, rowY + 4, 28, '×', () => { localMusic.remove(index); this.render(); }, 0x3d161c);
      });
    }
    const localControlsY = localY + (visibleLocal.length ? 26 + visibleLocal.length * 25 : 26) + 4;
    this.playlistButton(CX + 330, localControlsY, 96, local.shuffle ? 'SHUFFLE ON' : 'SHUFFLE OFF', () => { localMusic.toggleShuffle(); this.render(); }, local.shuffle ? 0x1d4a5c : 0x152028);
    this.add.text(CX + 434, localControlsY, 'Use the music toggle above to pause/resume the game score.', { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6a7a88' });
    if (local.tracks.length > visibleLocal.length) this.add.text(CX, localControlsY + 22, `+ ${local.tracks.length - visibleLocal.length} MORE LOCAL TRACKS`, { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6a7a88' });
    this.playlistButton(CX + 330, 568, 96, 'CLOSE', () => { this.playlistOpen = false; this.render(); });
  }

  private playlistButton(x: number, y: number, width: number, label: string, action: () => void, colour = 0x152028): void {
    const button = this.add.rectangle(x, y, width, 20, colour, 1).setStrokeStyle(1, colour === 0x3d161c ? 0xff6a6a : 0x335563).setInteractive({ useHandCursor: true });
    addRetroButtonHover(button, colour);
    this.add.text(x, y, label, { fontFamily: 'Consolas, monospace', fontSize: '7px', color: '#c8e9f5' }).setOrigin(0.5);
    button.on('pointerdown', () => { audio.click(); action(); });
  }

  private renderControls(_g: Phaser.GameObjects.Graphics): void {
    this.add.text(40, 96, 'DESKTOP KEYBOARD IS THE INTENDED SCHEME · TOUCH SUPPORT IS LIMITED', {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#d4a24a',
    });
    const left: [string, string][] = [
      ['1 - 8', 'Build / select tower'],
      ['SPACE', 'Call next wave'],
      ['Q', 'Spear airstrike'],
      ['W', 'Hardlight barrier'],
      ['E', 'Kite-3 drone move'],
      ['R', 'EMP lance'],
      ['TAB', 'Cycle drone mode'],
      ['H', 'Field manual'],
    ];
    const right: [string, string][] = [
      ['Click pad', 'Place selected tower'],
      ['Click tower', 'Open tower menu'],
      ['U', 'Upgrade selected'],
      ['T', 'Cycle target priority'],
      ['S', 'Sell selected'],
      ['X', 'Evolution / tune module'],
      ['B', 'Upgrade base / roof pads'],
      ['F', 'Fullscreen'],
      ['ESC', 'This menu'],
    ];
    const draw = (x: number, rows: [string, string][]) => {
      rows.forEach((r, i) => {
        const y = 150 + i * 40;
        this.add.text(x, y, r[0], {
          fontFamily: 'Consolas, monospace',
          fontSize: '12px',
          color: '#5ec8ff',
        });
        this.add.text(x + 110, y, r[1], {
          fontFamily: 'Consolas, monospace',
          fontSize: '12px',
          color: '#9fb2c0',
        });
      });
    };
    draw(CX, left);
    draw(CX + CW / 2, right);
  }

  // ---- actions ----

  /** True when the staged draft differs from what is currently saved. */
  private hasPending(): boolean {
    return JSON.stringify(this.draft) !== JSON.stringify(saveManager.data.settings);
  }

  /** Commit the staged settings and apply them live. */
  private commit(): void {
    const prevChatty = saveManager.data.settings.chatty;
    saveManager.data.settings = JSON.parse(JSON.stringify(this.draft)) as GameSettings;
    saveManager.save();
    this.applyAll();
    window.dispatchEvent(new CustomEvent('apogee-cursor-size'));
    window.dispatchEvent(new CustomEvent('apogee-audio-settings'));
    audio.click();

    // The tactical AI reacts to being told how talkative to be.
    if (this.draft.chatty !== prevChatty) {
      voice.play(this, `chatty_${this.draft.chatty}`, { force: true, priority: true, cooldown: 0 });
    }
    this.render();
  }

  private applyAll(): void {
    const s = saveManager.data.settings;
    audio.enabled = s.sfx;
    audio.volume = s.sfxVolume;
    music.applyEnabled(!!s.music, s.music ? s.musicVolume : 0);
    localMusic.setVolume(s.music ? s.musicVolume : 0);
    if (!s.music) localMusic.stop();
    const mode = s.scaleMode === 'fill' ? Phaser.Scale.ENVELOP : Phaser.Scale.FIT;
    if (this.game.scale.scaleMode !== mode) {
      this.game.scale.scaleMode = mode;
      this.game.scale.refresh();
    }
  }

  private resume(): void {
    audio.click();
    // Discard unapplied changes so the live settings stay consistent.
    this.scene.stop();
    this.scene.resume(this.from);
  }

  private restartMission(): void {
    audio.click();
    this.scene.stop();
    this.scene.resume(this.from);
    this.scene.get(this.from).scene.restart();
  }

  private toMenu(): void {
    audio.click();
    music.setMenuMode(true);
    if (this.inBattle) this.scene.stop(this.from);
    this.scene.stop('Command');
    // `start('Menu')` starts the target scene but does not stop this overlay.
    // Leaving Pause alive here meant its full-screen input layer continued to
    // sit above the menu, making the next deployment appear to freeze.
    this.scene.stop('Pause');
    this.scene.start('Menu');
  }
}

